import {
  Anchor,
  Box,
  Button,
  Container,
  Paper,
  PasswordInput,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState, useEffect } from 'react';
import { notifications } from '@mantine/notifications';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/useAuthStore';
import { specialLogin } from '../services/userManager';

interface LoginFormValues {
  email: string;
  password: string;
}

export default function LoginPartners() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loggingIn, setLoggingIn] = useState<boolean>(false);

  // Check if user is already logged in and redirect if necessary
  useEffect(() => {
    const { isLoggedIn, isInitialized } = useAuthStore.getState();

    if (isInitialized && isLoggedIn) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const form = useForm({
    mode: 'uncontrolled',
    initialValues: {
      email: '',
      password: '',
    },

    validate: {
      email: (value: string) =>
        /^\S+@\S+$/.test(value) ? null : t('invalid_email', 'Email non valida'),
      password: (value: string) =>
        value.length > 0 ? null : t('password_required', 'Password richiesta'),
    },
  });

  const handleLogin = async (values: LoginFormValues): Promise<void> => {
    setLoggingIn(true);

    try {
      const response = await specialLogin(values.email, values.password);

      if (response.authenticated) {
        // Fetch user data and update store
        const { fetchUser } = useAuthStore.getState();
        await fetchUser();

        navigate('/', { replace: true });
      } else {
        notifications.show({
          color: 'red',
          withBorder: true,
          radius: 'lg',
          title: t('login_error', 'Errore durante il login'),
          autoClose: false,
          message: response.message || 'Autenticazione fallita',
        });
      }
    } catch (error) {
      notifications.show({
        color: 'red',
        withBorder: true,
        radius: 'lg',
        title: t('login_error', 'Errore durante il login'),
        autoClose: false,
        message:
          error instanceof Error ? error.message : 'Unknown error occurred',
      });
    } finally {
      setLoggingIn(false);
    }
  };

  const onSubmit = (values: LoginFormValues) => {
    handleLogin(values).catch(() => {
      // Error already handled in handleLogin
    });
  };

  const getForgotPasswordMailto = () => {
    const email = form.getValues().email || '';
    const subject = encodeURIComponent('Password dimenticata');
    const body = encodeURIComponent(
      `Ho dimenticato la password.${
        email ? `\n\nLa mia email è: ${email}` : ''
      }`
    );
    return `mailto:admin@become-hub.com?subject=${subject}&body=${body}`;
  };

  return (
    <Container size={420} my={50}>
      <Title ta='center'>{t('login_title', 'Login')}</Title>

      <Paper withBorder shadow='md' p={30} mt={30} radius='sm'>
        <form onSubmit={form.onSubmit(onSubmit)}>
          <Box style={{ position: 'relative' }}>
            <TextInput
              label='Email'
              disabled={loggingIn}
              key={form.key('email')}
              {...form.getInputProps('email')}
              placeholder='email@example.com'
              required
            />
            <PasswordInput
              label='Password'
              disabled={loggingIn}
              key={form.key('password')}
              {...form.getInputProps('password')}
              placeholder='Inserisci la password'
              required
              mt='md'
            />
            <Box ta='right' mt='sm'>
              <Anchor
                href={getForgotPasswordMailto()}
                size='sm'
                c='dimmed'
                underline='hover'
              >
                Password dimenticata?
              </Anchor>
            </Box>
          </Box>
          <Button
            loading={loggingIn}
            disabled={loggingIn}
            fullWidth
            mt='xl'
            type='submit'
          >
            {t('login', 'Login')}
          </Button>
        </form>
        <Box ta='center' mt='md'>
          <Text size='sm' c='dimmed'>
            oppure
          </Text>
        </Box>
        <Box ta='center' mt='md'>
          <Anchor
            component='button'
            type='button'
            size='sm'
            c='dimmed'
            underline='hover'
            onClick={() => navigate('/login')}
          >
            Login with magic link
          </Anchor>
        </Box>
      </Paper>
    </Container>
  );
}
